import { useState, useEffect, useRef } from "react";
import { createFarm, getFarms } from "../api/farm-service/farmService";
import { initiateImageUpload, confirmCoverImageUpload, confirmGalleryUpload } from "../api/farm-service/imageService";
import { getCategories, getCategoryById } from "../api/farm-service/categoryService";
import { getFeatures, getFeatureById } from "../api/farm-service/featureService";
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import "../assets/stylesheets/Listing.css";


const initialHours = {
  Mon: { open: "10:00", close: "21:00", closed: false },
  Tue: { open: "10:00", close: "21:00", closed: false },
  Wed: { open: "10:00", close: "21:00", closed: false },
  Thu: { open: "10:00", close: "21:00", closed: false },
  Fri: { open: "10:00", close: "21:00", closed: false },
  Sat: { open: "10:00", close: "21:00", closed: false },
  Sun: { open: "10:00", close: "21:00", closed: false }
};


function isValidUrl(value) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

// Formats to "+1 (AAA) BBB CCCC"
function formatPhoneNumber(digits) {
  if (!digits) return '';
  let s = '+1';
  if (digits.length >= 1) s += ' (' + digits.slice(0, 3);
  if (digits.length >= 3) s += ')';
  if (digits.length > 3) s += ' ' + digits.slice(3, 6);
  if (digits.length > 6) s += ' ' + digits.slice(6, 10);
  return s;
}


async function canvasToWebPFile(file, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => (img.src = e.target.result);
    reader.onerror = () => reject(new Error('FileReader failed'));

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // canvas.toBlob is polyfilled by webp-hero when WebP isn't natively supported:
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(
              new File(
                [blob],
                file.name.replace(/\.\w+$/, '.webp'),
                { type: 'image/webp' }
              )
            );
          } else {
            reject(new Error('Canvas conversion failed'));
          }
        },
        'image/webp',
        quality
      );
    };
    img.onerror = () => reject(new Error('Image load failed'));

    reader.readAsDataURL(file);
  });
}


const initialFormState = {
  name: "",
  description: "",
  phoneNumber: "",
  webUrl: "",
  socialUrl: "",
  banner: "",
  tips: [{ tipTitle: "", tipText: "" }],
  categoryIds: [],
  featureIds: [],
  coverImage: null,
  coverPreview: "",
  images: [],
  imagePreviews: [],
  address: {
    street1: "",
    street2: "",
    city: "",
    region: "",
    state: "Ontario",
    zipcode: "",
    country: "Canada"
  },
  businessHours: initialHours
};


export default function FarmListing() {
  const [webpSupported, setWebpSupported] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [farms, setFarms] = useState([]);
  
  const [form, setForm] = useState(initialFormState);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  
  // store raw digits only
  const [rawPhone, setRawPhone] = useState('');
  // formatted display
  const formattedPhone = formatPhoneNumber(rawPhone);

  useEffect(() => {
    const initWebP = async () => {
      const checkWebPSupport = () => new Promise(resolve => {
        const canvas = document.createElement('canvas');
        canvas.width = 1; canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, 1, 1);
        canvas.toBlob(blob => resolve(!!blob), 'image/webp');
      });
      const supported = await checkWebPSupport();
      setWebpSupported(supported);
      if (!supported && typeof window !== 'undefined') {
        try {
          const { WebpMachine } = await import('webp-hero');
          await new WebpMachine().polyfillDocument();
          setWebpSupported(true);
        } catch {}
      }
    };
    initWebP();
  }, []);

  // Get Categories and Features from API 
  useEffect(() => {
    async function fetchData() {
      try {
        const categories = await getCategories();
        setAllCategories(categories.data);
      } catch (err) {
        console.error('Error loading categories', err);
      }
      try {
        const features = await getFeatures();
        setAllFeatures(features.data);
      } catch (err) {
        console.error('Error loading features', err);
      }
    }
    fetchData();
  }, []);

  // Remove image
  const removeCoverImage = () => {
    setForm(prev => ({
      ...prev,
      coverImage: null,
      coverPreview: ""
    }));
    // 
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const removeGalleryImage = (indexToRemove) => {
    setForm(prev => {
      const newImages = prev.images.filter((_, i) => i !== indexToRemove);
      const newPreviews = prev.imagePreviews.filter((_, i) => i !== indexToRemove);
      return {
        ...prev,
        images: newImages,
        imagePreviews: newPreviews
      };
    });
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };
  // Validate categories and features based on DTO requirements
  const validateCategoryOrFeature = (item) => {
    const errors = {};
    
    if (!item.name?.trim()) {
      errors.name = "Name cannot be empty";
    } else if (item.name.length < 2) {
      errors.name = "Name must be at least 2 characters long";
    } else if (item.name.length > 100) {
      errors.name = "Name cannot exceed 100 characters";
    }
    
    if (item.description && item.description.length > 1000) {
      errors.description = "Description cannot exceed 1000 characters";
    }
    
    return errors;
  };

  // Validate farm fields based on DTO requirements
  const validateFarmFields = (form) => {
    const errors = {};
    
    // Name validation
    if (!form.name.trim()) {
      errors.name = "Farm name cannot be empty";
    } else if (form.name.length < 2) {
      errors.name = "Farm name must be at least 2 characters long";
    } else if (form.name.length > 100) {
      errors.name = "Farm name cannot exceed 100 characters";
    }
    
    // Description validation
    if (form.description && form.description.length > 2000) {
      errors.description = "Description cannot exceed 2000 characters";
    }
    
    // Phone number validation
    if (form.phoneNumber && form.phoneNumber.length > 20) {
      errors.phoneNumber = "Phone number cannot exceed 20 characters";
    }
    
    // Web URL validation
    if (form.webUrl) {
      if (form.webUrl.length > 255) {
        errors.webUrl = "Web URL cannot exceed 255 characters";
      } else if (!isValidUrl(form.webUrl)) {
        errors.webUrl = "Web URL must be a valid URL";
      }
    }
    
    // Social URL validation
    if (form.socialUrl) {
      if (form.socialUrl.length > 255) {
        errors.socialUrl = "Social URL cannot exceed 255 characters";
      } else if (!isValidUrl(form.socialUrl)) {
        errors.socialUrl = "Social URL must be a valid URL";
      }
    }
    
    // Banner validation
    if (form.banner && form.banner.length > 50) {
      errors.banner = "Banner text cannot exceed 50 characters";
    }
    
    // Tips validation
    if (form.tips.length > 10) {
      errors.tips = "Maximum of 10 tips allowed";
    }
    
    // Category validation
    if (form.categoryIds.length > 3) {
      errors.categoryIds = "Maximum of 3 categories allowed per farm";
    }
    
    // Feature validation
    if (form.featureIds.length > 8) {
      errors.featureIds = "Maximum of 8 features allowed per farm";
    }
    
    return errors;
  };

  const handleCoverImage = async (e) => {
    const file = e.target.files[0];
    if (!file || file.size > 15 * 1024 * 1024) {
      return alert('Image must be less than 5MB');
    }
    try {
      const webpFile = await canvasToWebPFile(file);
      const preview = URL.createObjectURL(webpFile);
      setForm(prev => ({
        ...prev,
        coverImage: webpFile,
        coverPreview: preview
      }));
    } catch (err) {
      console.error('Cover image conversion failed:', err);
      alert('Failed: Cover image');
    }
  };

  const handleImages = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (form.images.length + newFiles.length < 4) {
      return alert('You must upload at least 4 images.');
    }

    const images = [...form.images];
    const previews = [...form.imagePreviews];

    for (const file of newFiles) {
      if (file.size > 15 * 1024 * 1024) {
        alert(`${file.name} exceeds 5MB`);
        continue;
      }
      try {
        const webpFile = await canvasToWebPFile(file);
        images.push(webpFile);
        previews.push(URL.createObjectURL(webpFile));
      } catch (err) {
        console.error(`Gallery conversion failed for ${file.name}:`, err);
        alert(`${file.name} Failed Gallery`);
      }
    }

    setForm(prev => ({ ...prev, images, imagePreviews: previews }));
  };



  const handleHourChange = (day, key, value) => {
    setForm(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [key]: value,
          closed: false 
        }
      }
    }));
  };

  const toggleClosed = (day) => {
    setForm(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          closed: !prev.businessHours[day].closed,
          open: prev.businessHours[day].closed ? "10:00" : null,
          close: prev.businessHours[day].closed ? "21:00" : null,
        }
      }
    }));
  };

  const handleTipsChange = (index, field, value) => {
    const newTips = [...form.tips];
    newTips[index] = { ...newTips[index], [field]: value };
    setForm({ ...form, tips: newTips });
  };

  // Validate tips based on backend requirements
  const validateTip = (tip) => {
    const errors = {};
    
    // Validate tipTitle
    if (!tip.tipTitle.trim()) {
      errors.tipTitle = "Tip title cannot be empty";
    } else if (tip.tipTitle.length < 2) {
      errors.tipTitle = "Tip title must be at least 2 characters long";
    } else if (tip.tipTitle.length > 100) {
      errors.tipTitle = "Tip title cannot exceed 100 characters";
    }
    
    // Validate tipText
    if (!tip.tipText.trim()) {
      errors.tipText = "Tip text cannot be empty";
    } else if (tip.tipText.length > 300) {
      errors.tipText = "Tip text cannot exceed 250 characters";
    }
    
    return errors;
  };

  const addTip = () => {
    if (form.tips.length < 7) {
      setForm((prev) => ({ ...prev, tips: [...prev.tips, { tipTitle: "", tipText: "" }] }));
    }
  };

  const removeTip = (index) => {
    const newTips = form.tips.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, tips: newTips }));
  };

  const handlePhoneChange = (e) => {
    const input = e.target;
    const { value, selectionStart } = input; // 현재 커서 위치를 가져옴

    // 숫자만 추출하고, '1'로 시작하면 제거, 최대 10자리로 제한
    const currentDigitsOnly = value.replace(/\D/g, '');
    const newRawPhone = (currentDigitsOnly.startsWith('1') ? currentDigitsOnly.slice(1) : currentDigitsOnly).slice(0, 10);
    
    // 이전 rawPhone 상태에서 커서 위치까지의 숫자 개수를 계산
    let digitsBeforeCursorOld = 0;
    for (let i = 0; i < selectionStart; i++) {
        // 현재 input value의 포맷된 문자열에서 숫자만 세어봄
        if (/\d/.test(value[i])) {
            digitsBeforeCursorOld++;
        }
    }

    // rawPhone 상태 업데이트 (실제 저장될 숫자)
    setRawPhone(newRawPhone);
    
    // 포맷된 전화번호 문자열 생성
    const newFormattedPhone = formatPhoneNumber(newRawPhone);
    
    // form 상태 업데이트 (표시될 전화번호)
    setForm(prev => ({
      ...prev,
      phoneNumber: newFormattedPhone
    }));

    // 커서 위치 재설정
    // DOM 업데이트 이후에 실행되도록 setTimeout 사용
    setTimeout(() => {
      if (phoneInputRef.current) {
        let newCursorPosition = 0;
        let digitsCount = 0;
        
        // 새로 포맷된 문자열에서 digitsBeforeCursorOld 개수만큼의 숫자가 있는 곳까지 이동
        for (let i = 0; i < newFormattedPhone.length; i++) {
          if (/\d/.test(newFormattedPhone[i])) {
            digitsCount++;
          }
          if (digitsCount === digitsBeforeCursorOld) {
            newCursorPosition = i + 1; // 해당 숫자 바로 뒤
            break;
          }
          newCursorPosition = i + 1; // 숫자가 아니더라도 계속 진행
        }

        // 만약 입력된 숫자의 끝에 커서가 있었다면, 새로 포맷된 문자열의 끝으로 커서를 이동
        if (digitsBeforeCursorOld === newRawPhone.length) {
          newCursorPosition = newFormattedPhone.length;
        }

        phoneInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };


  // Add validation check for the category/feature name when user attempts to select it
  function handleCheckbox(field, value) {
    const item = field === "categoryIds" 
      ? allCategories.find(cat => cat.id === value)
      : allFeatures.find(feat => feat.id === value);
    
    if (item) {
      const errors = validateCategoryOrFeature(item);
      if (Object.keys(errors).length > 0) {
        const errorMessages = Object.entries(errors)
          .map(([field, message]) => `${field}: ${message}`)
          .join("\n");
        alert(`Invalid ${field === "categoryIds" ? "category" : "feature"}: ${item.name}\n${errorMessages}`);
        return;
      }
    }
    
    setForm(prev => {
      const setIds = new Set(prev[field]);
      if (setIds.has(value)) setIds.delete(value);
      else setIds.add(value);
      return { ...prev, [field]: Array.from(setIds) };
    });
  }
  
  const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  const handleSubmit = async (e) => {
    e.preventDefault();
        
    const businessHoursDto = DAYS.map((day, idx) => ({
      dayOfWeek: idx,                  // 0=Mon, …, 6=Sun
      openTime: form.businessHours[day].open,
      closeTime: form.businessHours[day].close,
    }));

    const tipsDto = form.tips.map(tip => ({ 
      tipTitle: tip.tipTitle, 
      tipText: tip.tipText 
    }));

    const locationDto = {
      street1: form.address.street1,
      street2: form.address.street2,
      city: form.address.city,
      region: form.address.region,
      state: form.address.state,
      zipCode: form.address.zipcode,
      country: form.address.country,
    };

    const {
      name,
      description,
      tips,
      categoryIds,
      featureIds,
      coverImage,
      images,
      businessHours,
      address
    } = form;

    const missingFields = [];

    // Validate name & description
    if (!name.trim()) missingFields.push("Farm Name");
    if (!description.trim()) missingFields.push("Description");

    // Validate tips
    let tipErrors = false;
    const tipValidationErrors = [];
    
    if (tips.length === 0) {
      missingFields.push("At least one tip is required");
      tipErrors = true;
    } else {
      tips.forEach((tip, index) => {
        const errors = validateTip(tip);
        if (Object.keys(errors).length > 0) {
          tipErrors = true;
          for (const [field, message] of Object.entries(errors)) {
            tipValidationErrors.push(`Tip #${index + 1} ${field}: ${message}`);
          }
        }
      });
    }
    
    if (tipErrors) {
      if (tipValidationErrors.length > 0) {
        missingFields.push(...tipValidationErrors);
      } else {
        missingFields.push("Tips (both title and text required)");
      }
    }

    // Validate categories & features
    if (categoryIds.length === 0) {
      missingFields.push("At least one category is required");
    }
    
    if (featureIds.length === 0) {
      missingFields.push("At least one feature is required");
    }
    
    // Validate each selected category
    const categoryErrors = [];
    categoryIds.forEach(id => {
      const category = allCategories.find(cat => cat.id === id);
      if (category) {
        const errors = validateCategoryOrFeature(category);
        if (Object.keys(errors).length > 0) {
          for (const [field, message] of Object.entries(errors)) {
            categoryErrors.push(`Category '${category.name}' ${field}: ${message}`);
          }
        }
      }
    });
    
    // Validate each selected feature
    const featureErrors = [];
    featureIds.forEach(id => {
      const feature = allFeatures.find(feat => feat.id === id);
      if (feature) {
        const errors = validateCategoryOrFeature(feature);
        if (Object.keys(errors).length > 0) {
          for (const [field, message] of Object.entries(errors)) {
            featureErrors.push(`Feature '${feature.name}' ${field}: ${message}`);
          }
        }
      }
    });
    
    if (categoryErrors.length > 0) {
      missingFields.push(...categoryErrors);
    }
    
    if (featureErrors.length > 0) {
      missingFields.push(...featureErrors);
    }
    
    // Validate images
    if (!coverImage) missingFields.push("Cover Image");
    if (images.length === 0) missingFields.push("Gallery Images");

    // Validate phone number, web URL, social URL
    if (rawPhone.length < 10) {
      missingFields.push("Phone Number");
    }
    if (form.webUrl && !isValidUrl(form.webUrl)) missingFields.push("Web URL");
    if (form.socialUrl && !isValidUrl(form.socialUrl)) missingFields.push("Social URL");

    // Validate business hours strictly
    for (const [day, data] of Object.entries(businessHours)) {
      if (!data.closed) {
        if (!data.open || !data.close) {
          missingFields.push(`Business Hours: ${day} is missing open/close`);
        }
      }
    };

    // Validate full address
    const requiredAddressFields = {
      street1: "Street Address",
      city: "City",
      zipcode: "Zip Code",
      state: "Province",
      country: "Country"
    };

    Object.entries(requiredAddressFields).forEach(([key, label]) => {
      if (!address[key]?.trim()) {
        missingFields.push(`Address: ${label}`);
      }
    });

    // Show alert if validation fails
    if (missingFields.length > 0) {
      alert(
        `Please complete the following required fields:\n\n- ${missingFields.join(
          "\n- "
        )}`
      );
      return;
    }

    const payload = {
      farm: {
        name: form.name,
        description: form.description,
        phoneNumber: formattedPhone,
        webUrl: form.webUrl || null,
        socialUrl: form.socialUrl || null,
        banner: form.banner,
        businessHours: businessHoursDto,
        tips: tipsDto,
        categoryIds,
        featureIds
      },
      location: locationDto,
    };

    try {
      // 1) Farm Information
      const result = await createFarm(payload);
      const farm = result.data;
      const farmId = farm.id;

      // 5) Cover image upload
      const {
        data: { uploadUrl, id: imageId }
      } = await initiateImageUpload(farmId, {
        fileName: form.coverImage.name,
        fileType: form.coverImage.type,
        domain: "farm",
        ownerId: farmId,
      });

      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": form.coverImage.type },
        body: form.coverImage,
      });

      await confirmCoverImageUpload(farmId, imageId);

      // 6) Images upload
      const galleryIds = [];
      for (let file of form.images) {
        const {
          data: { uploadUrl, id: imageId }
        } = await initiateImageUpload(farmId, {
          fileName: file.name,
          fileType: file.type,
          domain: "farm",
          ownerId: farmId,
        });

        await fetch(uploadUrl, { 
          method: "PUT", 
          headers: { "Content-Type": file.type }, 
          body: file 
        });
        galleryIds.push(imageId);
      }

      await confirmGalleryUpload(farmId, galleryIds);
      
      // 7) Confirm success
      alert("Farm created successfully!");

      // Reset 
      setForm(initialFormState);
      setRawPhone("");

      if (coverInputRef.current) coverInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";

    } catch (err) {
      console.error('Submit error:', err);
      alert("Error creating farm. Please try again.");
    }
  };

  return (
    <form autoComplete="off" className="listing-form" onSubmit={handleSubmit}>
      <h2>Register a Farm</h2>
      
      <label>*Farm Name (2-100 characters)</label>
      <input
        name="name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        maxLength={100}
        required
      />

      <label>*Description (max 2000 characters)</label>
      <textarea
        name="description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        maxLength={2000}
      />

      <label>*Phone Number</label>
      <input
        ref={phoneInputRef}
        type="text"
        name="phoneNumber"
        value={formattedPhone}
        onChange={handlePhoneChange}
        placeholder="+1 (___) ___ ____"
        maxLength="18" 
      />

      <label>Web URL (max 255 characters)</label>
      <input
        type="url"
        name="webUrl"
        value={form.webUrl}
        onChange={e => setForm({ ...form, webUrl: e.target.value })}
        maxLength={255}
      />

      <label>Social URL (max 255 characters)</label>
      <input
        type="url"
        name="socialUrl"
        value={form.socialUrl}
        onChange={e => setForm({ ...form, socialUrl: e.target.value })}
        maxLength={255}
      />

      <label>Banner (max 50 characters)</label>
      <select
        name="banner"
        value={form.banner}
        onChange={(e) => setForm({ ...form, banner: e.target.value })}
      >
        <option value="">Select Banner</option>
        <option value="New">New</option>
        <option value="Staff Pick">Staff Pick</option>
        <option value="Trending">Trending</option>
      </select>

      <label>*Tips (max 10)</label>
      <div className="tip-list-input">
        {form.tips.map((tip, index) => (
          <div key={index} className="tip-item-input">
            <input
              type="text"
              placeholder="Tip Title (2-100 chars)"
              value={tip.tipTitle}
              onChange={(e) => handleTipsChange(index, "tipTitle", e.target.value)}
              maxLength={100}
            />
            <input
              type="text"
              placeholder="Tip Text (max 250 chars)"
              value={tip.tipText}
              onChange={(e) => handleTipsChange(index, "tipText", e.target.value)}
              maxLength={250}
            />
            <button type="button" onClick={() => removeTip(index)}>
              X
            </button>
          </div>
        ))}
        {form.tips.length < 10 && (
          <button type="button" onClick={addTip}>
            + Add Tip
          </button>
        )}
      </div>

      <fieldset className="form-lists">
        <legend>*Categories (max 3)</legend>
        {form.categoryIds.length >= 3 && (
          <p className="validation-message">Maximum of 3 categories allowed</p>
        )}
        {allCategories.map(cat => (
          <label key={cat.id} className={form.categoryIds.length >= 3 && !form.categoryIds.includes(cat.id) ? "disabled" : ""}>
            <input
              type="checkbox"
              checked={form.categoryIds.includes(cat.id)}
              onChange={() => handleCheckbox("categoryIds", cat.id)}
              disabled={form.categoryIds.length >= 3 && !form.categoryIds.includes(cat.id)}
            />
            {cat.name}
          </label>
        ))}
      </fieldset>

      <fieldset className="form-lists">
        <legend>*Features (max 8)</legend>
        {form.featureIds.length >= 8 && (
          <p className="validation-message">Maximum of 8 features allowed</p>
        )}
        {allFeatures.map(feat => (
          <label key={feat.id} className={form.featureIds.length >= 8 && !form.featureIds.includes(feat.id) ? "disabled" : ""}>
            <input
              type="checkbox"
              checked={form.featureIds.includes(feat.id)}
              onChange={() => handleCheckbox("featureIds", feat.id)}
              disabled={form.featureIds.length >= 8 && !form.featureIds.includes(feat.id)}
            />
            {feat.name}
          </label>
        ))}
      </fieldset>

      <label>*Cover Image (max 5MB)</label>
      <input type="file" accept="image/*" onChange={handleCoverImage} />
      {form.coverPreview && (
        <div className="image-preview-wrapper">
          <div className="image-wrapper">
            <img src={form.coverPreview} alt="Cover Preview" />
            <button
              type="button"
              className="remove-image-btn"
              onClick={removeCoverImage}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <label>*Gallery Images (min 4 images, max 5MB each)</label>
      <input type="file" multiple accept="image/*" onChange={handleImages} />
      {form.imagePreviews.length > 0 && (
        <div className="image-preview-wrapper">
          {form.imagePreviews.map((src, i) => (
            <div key={i} className="image-wrapper">
              <img src={src} alt={`Gallery Preview ${i + 1}`} />
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => removeGalleryImage(i)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <fieldset>
        <legend>*Business Hours</legend>
        {Object.entries(form.businessHours).map(([day, data]) => (
          <div key={day} className="day-row">
            <strong>{day}</strong>
            <input
              type="time"
              value={data.open || ""}
              disabled={data.closed}
              onChange={(e) => handleHourChange(day, "open", e.target.value)}
            />
            <span>to</span>
            <input
              type="time"
              value={data.close || ""}
              disabled={data.closed}
              onChange={(e) => handleHourChange(day, "close", e.target.value)}
            />
            <label className="closed-toggle">
              <input
                type="checkbox"
                checked={data.closed}
                onChange={() => toggleClosed(day)}
              />
              Closed
            </label>
          </div>
        ))}
      </fieldset>

      <fieldset className="address">
        <label>*Address</label>
        {Object.entries(form.address).map(([key, val]) => (
          <input
            key={key}
            name={key}
            placeholder={key.charAt(0).toUpperCase() + key.slice(1)}
            value={val}
            onChange={(e) =>
              setForm({
                ...form,
                address: { ...form.address, [key]: e.target.value }
              })
            }
          />
        ))}
      </fieldset>

      <button type="submit">Submit Farm Info</button><br/>
    </form>
  );
}